import { applySnapshot, types, flow } from 'mobx-state-tree'
import { apiV1 } from 'utils'

const VERSION_HEADER = { 'x-version': '1.0' }

export const CriticalSectionStore = types
  .model('CriticalSectionStore', {
    _list: types.optional(types.array(types.frozen()), []),
    _enumList: types.optional(types.array(types.frozen()), []),
    _pendingCount: 0,
  })
  .views(self => ({
    get isPending() {
      return self._pendingCount > 0
    },
    get list() {
      return self._list
    },
    get enumList() {
      return self._enumList
    },
  }))
  .actions(self => ({
    startPending() {
      self._pendingCount++
    },
    endPending() {
      self._pendingCount = Math.max(0, self._pendingCount - 1)
    },
    setList(list) {
      applySnapshot(self._list, Array.isArray(list) ? list : [])
    },

    clearList() {
      applySnapshot(self._list, [])
    },

    // GET /api/CriticalSections?culture=
    getEnumList: flow(function* (culture) {
      self.startPending()
      try {
        const res = yield apiV1.get('/CriticalSections', { params: { culture }, headers: VERSION_HEADER })
        applySnapshot(self._enumList, Array.isArray(res.data) ? res.data : [])
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // GET /api/BankCriticalSections/{bankId}?culture=
    getListByBankId: flow(function* (bankId, culture) {
      self.startPending()
      try {
        const res = yield apiV1.get(`/BankCriticalSections/${bankId}`, { params: { culture }, headers: VERSION_HEADER })
        self.setList(res.data)
        return Array.isArray(res.data) ? res.data : []
      } finally {
        self.endPending()
      }
    }),

    // GET /api/BankCriticalSections?id=&culture=
    getById: flow(function* (id, culture) {
      self.startPending()
      try {
        const res = yield apiV1.get('/BankCriticalSections', { params: { id, culture }, headers: VERSION_HEADER })
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // GET /api/BankCriticalSections/openviewpwd?id=  — returns { id, password }
    openView: flow(function* (id) {
      self.startPending()
      try {
        const res = yield apiV1.get('/BankCriticalSections/openviewpwd', { params: { id }, headers: VERSION_HEADER })
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // PATCH /api/BankCriticalSections  — add (body: bankId as integer)
    add: flow(function* (bankId) {
      self.startPending()
      try {
        const res = yield apiV1.patch('/BankCriticalSections', bankId, {
          headers: { ...VERSION_HEADER, 'Content-Type': 'application/json' },
        })
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // PATCH /api/BankCriticalSections/changepassword
    changePassword: flow(function* (data) {
      self.startPending()
      try {
        const res = yield apiV1.patch('/BankCriticalSections/changepassword', data, { headers: VERSION_HEADER })
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // PATCH /api/BankCriticalSections/resetpassword?id=
    resetPassword: flow(function* (id) {
      self.startPending()
      try {
        const res = yield apiV1.patch('/BankCriticalSections/resetpassword', null, { params: { id }, headers: VERSION_HEADER })
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // PATCH /api/BankCriticalSections/activation?id=
    toggleActivation: flow(function* (id) {
      self.startPending()
      try {
        const res = yield apiV1.patch('/BankCriticalSections/activation', null, { params: { id }, headers: VERSION_HEADER })
        return res.data
      } finally {
        self.endPending()
      }
    }),

    // DELETE /api/BankCriticalSections/{id}
    remove: flow(function* (id) {
      self.startPending()
      try {
        const res = yield apiV1.delete(`/BankCriticalSections/${id}`, { headers: VERSION_HEADER })
        return res.data
      } finally {
        self.endPending()
      }
    }),
  }))

export default CriticalSectionStore
